import { TasksService } from "@/services/v2/tasks";
import { ref, computed, onMounted, watch } from "vue";
import { Task, FishTransfer, TaskFishTransfer } from "@/types/v2";
import { useTask } from "../useTask";
import { useNotifications } from "@/composables/useNotifications";

export function useFishTransfer() {
  const { activeTask } = useTask();
  const { showTaskCompletionSuccess, showWarning, showError } = useNotifications();

  const transfersData = ref<FishTransfer[]>([]);
  const actualTransfers = ref<FishTransfer[]>([]);
  const globalTransferAmount = ref(0);
  const modals = ref({
    instructions: false,
    transferLog: false,
  });

  const isFishTransferTask = (task: Task | null): task is TaskFishTransfer =>
    task?.objective.id === "fish_transfer";

  const transferInfo = computed(() => {
    if (!isFishTransferTask(activeTask.value)) return undefined;

    const { transferType, transfers } = activeTask.value.details;
    const isSingleOperation = transferType !== "transfer";

    if (isSingleOperation) {
      const transfer = transfers[0];
      if (!transfer) return undefined;

      const isFillingOperation = transferType === "filling";
      return {
        action: {
          upper: [isFillingOperation ? "Add" : "Remove"],
          lower: [isFillingOperation ? "add" : "remove"],
          past: [isFillingOperation ? "added" : "removed"],
        },
        amount: Math.abs(transfer.amount),
        headline: transferType.charAt(0).toUpperCase() + transferType.slice(1),
        prefix: [isFillingOperation ? "To" : "From"],
        description: {
          main: `${isFillingOperation ? "Add" : "Remove"} ${Math.abs(
            transfer.amount
          )} fish${isFillingOperation ? " to " : " from "}tank "${
            transfer.tank.label
          } (${transfer.tank.room})"`,
          additional: isFillingOperation
            ? "Assign the following tag to all new fish: "
            : undefined,
        },
      };
    }

    const [from, to] = transfers.sort((a, b) => a.amount - b.amount);
    if (!from || !to) return undefined;

    return {
      action: {
        upper: ["Remove", "Add", "Transfer"],
        lower: ["remove", "add", "transfer"],
        past: ["removed", "added", "transferred"],
      },
      amount: Math.abs(from.amount),
      headline: transferType.charAt(0).toUpperCase() + transferType.slice(1),
      prefix: ["From", "To"],
      description: {
        main: `Transfer ${Math.abs(from.amount)} fish from tank "${
          from.tank.label
        } (${from.tank.room})" to tank "${to.tank.label} (${to.tank.room})"`,
        additional: `Assign the following tag to all new fish in tank "${to.tank.label} (${to.tank.room})": `,
      },
    };
  });

  const transferBalance = computed(() =>
    transfersData.value.length > 0
      ? Math.abs(actualTransfers.value[0]?.actualTransfer)
      : 0
  );

  const newTags = computed(() => {
    if (!isFishTransferTask(activeTask.value)) return [];

    const { transferType } = activeTask.value.details;

    if (transferType === "transfer" && actualTransfers.value.length >= 2) {
      const [source, destination] = actualTransfers.value;
      const sourceTagKeys =
        source?.animalGroup?.tags?.map((tag) => tag.key) || [];
      return (destination?.animalGroup?.tags || []).filter(
        (tag) => !sourceTagKeys.includes(tag.key)
      );
    }

    if (transferType === "filling") {
      return actualTransfers.value[0]?.animalGroup?.tags || [];
    }

    return [];
  });

  const updateFishCount = (amount: number) => {
    if (!isFishTransferTask(activeTask.value)) return;

    const { transferType } = activeTask.value.details;
    const transfers = [...actualTransfers.value];

    if (transferType !== "transfer") {
      const transfer = {
        ...transfers[0],
        actualTransfer: (transfers[0].actualTransfer || 0) + amount,
      };
      updateTransfer(transfer);
      actualTransfers.value = [transfer];
      return;
    }

    const [source, destination] = transfers;
    if (!source || !destination) return;

    const updatedTransfers = [
      { ...source, actualTransfer: (source.actualTransfer || 0) - amount },
      {
        ...destination,
        actualTransfer: (destination.actualTransfer || 0) + amount,
      },
    ];

    updatedTransfers.forEach((transfer) => updateTransfer(transfer));
    actualTransfers.value = updatedTransfers;
  };

  const addCommentOnTask = (comment: string, amount: number): void => {
    if (!activeTask.value || !isFishTransferTask(activeTask.value))
      return;

    activeTask.value.details.transfers.forEach((transfer: FishTransfer) => {
      transfer.progressData = [];
      transfer.progressData.push({
        amount: transfer.amount > 0 ? amount : amount * -1,
        comment: comment,
      });
    });
  };

  const handleCompleteTask = async () => {
    if (!isFishTransferTask(activeTask.value)) return;

    const compareTransfer = actualTransfers.value[0];
    const explanationExists =
      activeTask.value?.details?.transfers[0]?.progressData?.length > 0;

    if (
      Math.abs(compareTransfer.amount) !==
        Math.abs(compareTransfer.actualTransfer) &&
      !explanationExists
    ) {
      showWarning("Transfer Discrepancy", "The actual transfer amount doesn't match the target amount. Please provide an explanation.");
      modals.value.transferLog = true;
      return;
    }

    if (!activeTask.value) return;

    try {
      if (activeTask.value.details.transferType === "removal") {
        activeTask.value.details.transfers[0].actualTransfer *= -1;
      }

      await TasksService.finishTask(activeTask.value);
      showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
      console.error('Error completing animal transfer task:', error);
      showError("Task Completion Failed", "Failed to complete the animal transfer task. Please try again.");
    }
  };

  const updateTransfer = (transfer: FishTransfer) => {
    if (activeTask.value && isFishTransferTask(activeTask.value)) {
      const index = activeTask.value.details.transfers.findIndex(
        (t: FishTransfer) => t.key === transfer.key
      );
      if (index !== -1) {
        activeTask.value.details.transfers[index] = transfer;
      }
    }
  };

  const performOperation = () => {
    if (globalTransferAmount.value > 0) {
      updateFishCount(globalTransferAmount.value);
      globalTransferAmount.value = 0;
    } else if (globalTransferAmount.value === 0) {
      showWarning("Invalid Amount", "Please enter a valid transfer amount greater than 0.");
    }
  };

  onMounted(() => {
    if (!isFishTransferTask(activeTask.value)) return;

    const transfers = activeTask.value.details.transfers;
    transfersData.value = Array.isArray(transfers) ? transfers : [transfers];
    actualTransfers.value = transfersData.value
      .sort((a, b) => a.amount - b.amount)
      .map((transfer) => ({
        ...transfer,
        actualTransfer: transfer.actualTransfer || 0,
      }));
  });

  watch(
    () =>
      isFishTransferTask(activeTask.value)
        ? activeTask.value.details.transfers
        : undefined,
    (newTransfers: FishTransfer[] | FishTransfer | undefined) => {
      if (!newTransfers) return;

      transfersData.value = Array.isArray(newTransfers)
        ? newTransfers
        : [newTransfers];
      actualTransfers.value = transfersData.value.map((transfer) => ({
        ...transfer,
        actualTransfer: transfer.actualTransfer || 0,
      }));
    },
    { deep: true }
  );

  return {
    transferInfo,
    actualTransfers,
    globalTransferAmount,
    performOperation,
    addCommentOnTask,
    transferBalance,
    newTags,
    handleCompleteTask,
    instructionsModal: computed(() => modals.value.instructions),
    toggleInstructionsModal: () =>
      (modals.value.instructions = !modals.value.instructions),
    fishTransferLogModal: computed(() => modals.value.transferLog),
    toggleFishTransferLogModal: () =>
      (modals.value.transferLog = !modals.value.transferLog),
  };
}
